import type * as React from "react";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "@/components/ui/atoms/avatar";
import { Markdown } from "@/components/ui/atoms/markdown";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/atoms/tooltip";
import { cn } from "@/lib/utils";

type MessageProps = {
  children: React.ReactNode;
  className?: string;
} & React.ComponentProps<"div">;

function Message({ children, className, ...props }: MessageProps) {
  return (
    <div className={cn("flex items-start gap-3", className)} {...props}>
      {children}
    </div>
  );
}

type MessageAvatarProps = {
  src: string;
  alt: string;
  fallback?: string;
  className?: string;
};

function MessageAvatar({ src, alt, fallback, className }: MessageAvatarProps) {
  return (
    <Avatar className={cn("h-8 w-8 shrink-0", className)}>
      <AvatarImage alt={alt} src={src} />
      {fallback && <AvatarFallback>{fallback}</AvatarFallback>}
    </Avatar>
  );
}

type MessageContentProps = {
  children: React.ReactNode;
  markdown?: boolean;
  className?: string;
} & React.ComponentProps<typeof Markdown> &
  React.ComponentProps<"div">;

function MessageContent({
  children,
  markdown = false,
  className,
  ...props
}: MessageContentProps) {
  const classNames = cn(
    "prose whitespace-normal break-words rounded-lg bg-secondary px-4 py-2 text-foreground",
    className
  );

  return markdown ? (
    <Markdown className={classNames} {...props}>
      {children as string}
    </Markdown>
  ) : (
    <div className={classNames} {...props}>
      {children}
    </div>
  );
}

type MessageActionsProps = {
  children: React.ReactNode;
  className?: string;
} & React.ComponentProps<"div">;

function MessageActions({
  children,
  className,
  ...props
}: MessageActionsProps) {
  return (
    <div
      className={cn("flex items-center gap-2 text-muted-foreground", className)}
      {...props}
    >
      {children}
    </div>
  );
}

type MessageActionProps = {
  className?: string;
  tooltip: React.ReactNode;
  children: React.ReactElement;
  side?: "top" | "bottom" | "left" | "right";
} & React.ComponentProps<typeof Tooltip>;

function MessageAction({
  tooltip,
  children,
  className,
  side = "top",
  ...props
}: MessageActionProps) {
  return (
    <TooltipProvider>
      <Tooltip {...props}>
        <TooltipTrigger render={children} />
        <TooltipContent className={className} side={side}>
          {tooltip}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export type {
  MessageActionProps,
  MessageActionsProps,
  MessageAvatarProps,
  MessageContentProps,
  MessageProps,
};
export {
  Message,
  MessageAction,
  MessageActions,
  MessageAvatar,
  MessageContent,
};
